import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { X, Plus } from 'lucide-react';

/**
 * AccessoryPicker
 *
 * Tappable chip picker for selecting common accessories supplied with a job.
 * - Tap a suggestion chip to toggle that accessory on/off
 * - Use the "Other" input to add custom items not in the list
 * - Selected items render as removable tags
 *
 * Props:
 *   value: string[]        - currently selected accessories
 *   onChange: (string[]) => void
 *   options: string[]      - suggestion chips (from Settings)
 */
export const AccessoryPicker = ({ value = [], onChange, options = [] }) => {
  const [customInput, setCustomInput] = useState('');

  const selected = Array.isArray(value) ? value : [];

  const toggle = (item) => {
    const trimmed = item.trim();
    if (!trimmed) return;
    // Case-insensitive match to detect duplicates
    const exists = selected.find((s) => s.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      onChange(selected.filter((s) => s.toLowerCase() !== trimmed.toLowerCase()));
    } else {
      onChange([...selected, trimmed]);
    }
  };

  const removeItem = (item) => {
    onChange(selected.filter((s) => s !== item));
  };

  const addCustom = () => {
    const trimmed = customInput.trim();
    if (!trimmed) return;
    const exists = selected.find((s) => s.toLowerCase() === trimmed.toLowerCase());
    if (!exists) {
      onChange([...selected, trimmed]);
    }
    setCustomInput('');
  };

  const isSelected = (item) =>
    selected.some((s) => s.toLowerCase() === item.toLowerCase());

  return (
    <div className="space-y-3" data-testid="accessory-picker">
      {/* Suggestion chips */}
      {options.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="accessory-option-chips">
          {options.map((opt) => {
            const active = isSelected(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                data-testid={`accessory-option-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                className={
                  'px-3 py-1.5 rounded-full text-sm border transition-colors ' +
                  (active
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100')
                }
              >
                {active ? '\u2713 ' : '+ '}{opt}
              </button>
            );
          })}
        </div>
      )}

      {/* Custom 'Other' input */}
      <div className="flex gap-2">
        <Input
          placeholder="Other accessory (e.g. Adapter)"
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
          data-testid="accessory-custom-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={addCustom}
          data-testid="accessory-custom-add-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      {/* Selected tags display */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="accessory-selected-list">
          {selected.map((item) => (
            <span
              key={item}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-800 border border-blue-200 rounded-md text-sm"
              data-testid={`accessory-selected-${item.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {item}
              <button
                type="button"
                onClick={() => removeItem(item)}
                className="hover:text-blue-950"
                aria-label={`Remove ${item}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccessoryPicker;
